<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8 text-white">
              <h2 class="page-title"><i class="fe fe-users mr-2"></i>Manage Dependents</h2>
              <p class="page-subtitle" v-if="client && client.full_name">
                For <strong>{{ client.full_name }}</strong>
                <span class="ml-2" style="opacity:.7;">ll {{ client.id_card_number }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── ADD / EDIT FORM ──────────────────────────────── -->
        <div class="card form-card shadow mb-4">

          <div class="form-card-header">
            <div>
              <h5 class="mb-0 font-weight-bold text-white">
                {{ edit ? 'Edit Dependent' : 'Add New Dependent' }}
              </h5>
              <p class="mb-0 mt-1 text-white" style="opacity:.8; font-size:.85rem;">
                {{ edit ? 'Update the details below and save' : 'Fill in the details to register a dependent' }}
              </p>
            </div>
            <button class="form-close-btn" type="button" @click="clearIt">
              <i class="fe fe-x"></i>
            </button>
          </div>

          <div class="card-body px-4 pb-4">

            <!-- Step 1: Relationship & Identity -->
            <div class="form-section">
              <div class="form-section-title">
                <span class="section-number">1</span> Relationship &amp; Identity
              </div>
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="form-label">Relationship Type <span class="text-danger">*</span></label>
                  <select class="form-control styled-select" v-model="dependent.relationShipType"
                    required @change="isExtraSelector()">
                    <option value="">-- Select relationship --</option>
                    <option v-for="rel in filteredRelationships" :value="rel" :key="rel">{{ rel }}</option>
                  </select>
                  <small v-if="filteredRelationships.length === 0" class="text-warning mt-1 d-block">
                    <i class="fe fe-alert-circle mr-1"></i> All relationship slots are filled
                  </small>
                </div>
                <div class="form-group col-md-6">
                  <label class="form-label">Gender <span class="text-danger">*</span></label>
                  <select class="form-control styled-select" v-model="dependent.gender" required>
                    <option value="">-- Select gender --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="form-label">Surname <span class="text-danger">*</span></label>
                  <input type="text" class="form-control"
                    v-model="dependent.lastname" placeholder="Surname"
                    :required="dependent.relationShipType === 'Spouse'" />
                </div>
                <div class="form-group col-md-4">
                  <label class="form-label">First Name <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" required
                    placeholder="First Name" v-model="dependent.firstname" />
                </div>
                <div class="form-group col-md-4">
                  <label class="form-label">Middle Name</label>
                  <input type="text" class="form-control" placeholder="Middle Name"
                    v-model="dependent.middle_name" />
                </div>
              </div>
            </div>

            <!-- Step 2: Personal Details -->
            <div class="form-section">
              <div class="form-section-title">
                <span class="section-number">2</span> Personal Details
              </div>
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="form-label">Date of Birth <span class="text-danger">*</span></label>
                  <input type="date" class="form-control styled-select" required
                    :max="dobMaxDate" v-model="dependent.dob" />
                  <small v-if="dependent.relationShipType === 'Spouse'" class="text-info d-block mt-1">
                    <i class="fe fe-info mr-1"></i> Spouse must be at least 18 years old
                  </small>
                  <small v-else-if="dependent.relationShipType && dependent.relationShipType !== 'Extra Dependent'" class="text-muted d-block mt-1">
                    <i class="fe fe-info mr-1"></i> Child dependent must be under 21 years old
                  </small>
                </div>
                <div class="form-group col-md-6">
                  <label class="form-label">Phone Number</label>
                  <input v-phone type="text" class="form-control" 
                    placeholder="Mobile number" v-model="dependent.phone_number" />
                </div>
                <div class="form-group col-md-6">
                  <label class="form-label">NIN</label>
                  <input type="text" class="form-control"
                    placeholder="National Identification Number" v-model="dependent.nimc_number" />
                </div>
                <div class="form-group col-md-6">
                  <label class="form-label">Blood Group</label>
                  <select class="form-control styled-select" v-model="dependent.blood">
                    <option value="NA">NA</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
                <div class="form-group col-md-6" v-if="agency.id == '1539642'">
                  <label class="form-label">Additional Dependent?</label>
                  <select class="form-control styled-select" v-model="dependent.is_addtional">
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Step 3: Photo -->
            <div class="form-section">
              <div class="form-section-title">
                <span class="section-number">3</span> Photo
                <span class="text-muted font-weight-normal ml-1" style="font-size:.8rem;">(Optional)</span>
              </div>
              <div class="d-flex align-items-center flex-wrap mb-3" style="gap: 10px;">
                <button class="btn btn-outline-primary" type="button" @click="takePicAndroid">
                  <i class="fe fe-camera mr-1"></i> Capture
                </button>
                <label class="btn btn-outline-secondary mb-0" style="cursor: pointer;">
                  <i class="fe fe-upload mr-1"></i> Upload
                  <input type="file" accept="image/*" style="display: none;" @change="attachPic" />
                </label>
                <button v-if="dependent.user_image && photoOriginal" class="btn btn-outline-secondary btn-sm"
                  type="button" @click="cropPhoto(photoOriginal)">
                  <i class="fe fe-crop mr-1"></i> Adjust
                </button>
                <button v-if="dependent.user_image" class="btn btn-outline-danger btn-sm" type="button"
                  @click="removePhoto">
                  <i class="fe fe-x mr-1"></i> Remove
                </button>
              </div>
              <div v-if="dependent.user_image" class="d-flex align-items-center" style="gap: 14px;">
                <img :src="dependent.user_image"
                  style="width: 100px; height: 129px; object-fit: cover; border-radius: 10px; border: 2px solid #2563eb;" />
                <div>
                  <p class="text-success mb-1 font-weight-bold" style="font-size:.85rem;">
                    <i class="fe fe-check-circle mr-1"></i>Photo ready
                  </p>
                  <p class="text-muted mb-0" style="font-size:.8rem;">Will be saved with the dependent</p>
                </div>
              </div>
              <div v-else class="upload-placeholder">
                <i class="fe fe-image" style="font-size: 1.6rem; opacity:.3;"></i>
                <small class="text-muted mt-1 d-block">No photo selected</small>
              </div>
            </div>

            <!-- Payment Session Notice -->
            <div v-if="!edit && String(agency.id) === '313923' && paymentSessionActive"
              class="alert alert-success d-flex align-items-center mb-3" style="border-radius: 8px;">
              <i class="fe fe-check-circle mr-2"></i>
              <span>
                Payment session active — you can add more dependents for free for the next
                <strong>{{ paymentSessionMinutesLeft }} minute{{ paymentSessionMinutesLeft === 1 ? '' : 's' }}</strong>.
              </span>
            </div>

            <!-- Payment Notice -->
            <div v-if="requiresDependentPayment" class="alert alert-warning d-flex align-items-center mb-3"
              style="border-radius: 8px;">
              <i class="fe fe-credit-card mr-2"></i>
              <span v-if="dependent.relationShipType === 'Extra Dependent'">
                A payment of <strong>&#8358;{{ dependentPaymentAmount }}</strong> is required to add an Extra Dependent.
              </span>
              <span v-else>
                This dependent is being added on a different date from {{ client.full_name }}'s enrollment
                (<strong>{{ client.created_at | moment("MMM Do YYYY") }}</strong>). A payment of
                <strong>&#8358;{{ dependentPaymentAmount }}</strong> is required to add this dependent.
              </span>
            </div>

            <!-- Actions -->
            <div class="d-flex justify-content-end" style="gap: 10px;">
              <button type="button" class="btn btn-cancel" @click="clearIt">
                <i class="fe fe-x mr-1"></i> {{ edit ? 'Cancel Edit' : 'Clear' }}
              </button>
              <button v-if="!requiresDependentPayment" type="button" class="btn btn-create" @click="submitForm"
                :disabled="isLoading">
                <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Saving…</span>
                <span v-else>
                  <i :class="edit ? 'fe fe-save' : 'fe fe-user-plus'" class="mr-1"></i>
                  {{ edit ? 'Update Dependent' : 'Add Dependent' }}
                </span>
              </button>
              <paystack v-else :amount="dependentPaymentAmount * 100" :email="user.email" :subaccount="agency.virtual_account"
                :paystackkey="paystackkey" :reference="dependentPaymentReference"
                :callback="onDependentPaymentSuccess" :close="onDependentPaymentClose" :embed="false">
                <button type="button" class="btn btn-create"
                  :disabled="isLoading || !!dependentValidationError">
                  <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Saving…</span>
                  <span v-else><i class="fe fe-credit-card mr-1"></i> Pay &#8358;{{ dependentPaymentAmount }} &amp; Add Dependent</span>
                </button>
              </paystack>
            </div>
            <small v-if="requiresDependentPayment && dependentValidationError" class="text-danger d-block text-right mt-2">
              {{ dependentValidationError }}
            </small>

          </div>
        </div>

        <!-- ── DEPENDENTS LIST ──────────────────────────────── -->
        <div class="card codes-card shadow-sm">
          <div class="card-header-custom">
            <div class="d-flex align-items-center" style="gap: 10px;">
              <h5 class="mb-0"><i class="fe fe-users mr-2"></i>Enrolled Dependents</h5>
              <span class="badge badge-pill badge-secondary">{{ dependents ? dependents.length : 0 }}</span>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table codes-table mb-0">
                <thead>
                  <tr>
                    <th class="px-4">Photo</th>
                    <th>Name</th>
                    <th>Relationship</th>
                    <th>Date of Birth</th>
                    <th>Gender</th>
                    <th>Blood</th>
                    <th>Enrolled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!dependents || !dependents.length">
                    <td colspan="8" class="text-center empty-state">
                      <i class="fe fe-users" style="font-size:2rem;opacity:.3;"></i>
                      <p class="mt-2 mb-0">No dependents enrolled yet</p>
                    </td>
                  </tr>
                  <tr v-for="dep in dependents" :key="dep.id" class="code-row">
                    <td class="px-4">
                      <img v-if="dep.image" :src="dep.image_url" class="dep-avatar"
                        onerror="this.onerror=null; this.src='/assets/img/placeholder.svg'" />
                      <vue-initials-img v-else :name="dep.firstname + ' ' + dep.lastname"
                        class="dep-avatar-initials" size="38" />
                    </td>
                    <td>
                      <router-link :to="{ path: '/dependent/' + dep.id }">
                        <p class="mb-0 font-weight-bold text-info" style="font-size:.85rem;">
                          {{ dep.full_name }}
                        </p>
                      </router-link>
                      <small class="text-muted">{{ dep.id_card_number }}</small>
                    </td>
                    <td>
                      <span class="rel-badge">{{ dep.relationShipType }}</span>
                    </td>
                    <td style="font-size:.85rem;">{{ dep.dob }}</td>
                    <td style="font-size:.85rem;">{{ dep.gender }}</td>
                    <td>
                      <span class="blood-badge">{{ dep.blood || 'NA' }}</span>
                    </td>
                    <td style="font-size:.83rem;">{{ dep.created_at | moment("MMM Do YYYY") }}</td>
                    <td>
                      <div class="d-flex" style="gap: 4px;">
                        <router-link :to="{ path: '/dependent/' + dep.id }">
                          <button class="btn btn-sm btn-view"><i class="fe fe-eye"></i></button>
                        </router-link>
                        <button class="btn btn-sm btn-outline-info" @click="editDep(dep)">
                          <i class="fe fe-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="deleteDep(dep)">
                          <i class="fe fe-trash-2"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
      <PhotoCropper ref="photoCropper" />
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import { randomAlphaNumeric } from "@/utils/secureRandom";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import paystack from "vue-paystack";
import PhotoCropper from "@/components/PhotoCropper.vue";

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: { Navbar, Loading, paystack, PhotoCropper },
  data() {
    return {
      user: null,
      agency: agency,
      dependents: [],
      relationships: [
        "Spouse",
        "Child 1",
        "Child 2",
        "Child 3",
        "Child 4",
        // ZAMCHEMA has no paid "Extra Dependent" slot.
      ],
      client: "",
      edit: false,
      isLoading: false,
      fullPage: true,
      states: "",
      providers: "",
      state: "",
      lga_states: "",
      dependent_id: "",
      imagefile: "",
      image: "",
      upload_pic: false,
      photoOriginal: "",
      paystackkey: "pk_live_873084ab876aad40fe062000ee5a706de1b16f30", //paystack public key
      pendingPaymentRef: null,
      paymentSessionDurationMs: 60 * 60 * 1000,
      dependent: {
        user_image: "",
        firstname: "",
        lastname: "",
        middle_name: "",
        middlename: "",
        institution_attending: "",
        email: "",
        phone_number: "",
        relationShipType: "",
        gender: "",
        lga: "",
        dob: "",
        provider: "",
        nimc_number: "",
        blood: "NA",
        is_addtional: "0",
      },
    };
  },
  computed: {
    dobMaxDate() {
      const today = new Date();
      if (this.dependent.relationShipType === "Spouse") {
        const d = new Date(today);
        d.setFullYear(d.getFullYear() - 18);
        return d.toISOString().slice(0, 10);
      }
      return today.toISOString().slice(0, 10);
    },

    getAge() {
      const today = new Date();
      const birthDate = new Date(this.dependent.dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age;
    },

    getExpiry() {
      if (this.dependent.relationShipType === "Spouse A") {
        return this.client.expiry_date;
      }
      const today = new Date();
      const principalExpiry = new Date(this.client.expiry_date);
      const principalExpYear = principalExpiry.getFullYear();
      const remainingYears = 18 - this.getAge;
      const finaldate = today.getFullYear() + remainingYears;
      return finaldate > principalExpYear ? this.client.expiry_date : finaldate + "-12-31";
    },

    getIdNum() {
      const isYLS = String(agency.id) === "1539642";
      const base = (this.client && this.client.id_card_number) || "";

      const relNumMap = {
        "Spouse":  1,
        "Child 1": 2,
        "Child 2": 3,
        "Child 3": 4,
        "Child 4": 5,
      };

      // Extra Dependent numbers used to be assigned by counting how many
      // "Extra Dependent" rows already existed (+6). That collides with an
      // already-used id_card_number as soon as an extra dependent has ever
      // been deleted and re-added (the count drops, but the old number may
      // still be taken), which is a common real workflow and the likely
      // cause of "add fails whenever an extra dependent already exists".
      // Using the highest numeric suffix actually in use (across every
      // dependent, not just other Extra Dependents) avoids reusing a taken
      // number regardless of deletions or historical gaps.
      const nextExtraNum = (root) => {
        const used = (this.dependents || [])
          .map((d) => {
            const id = d.id_card_number || "";
            if (!id.startsWith(root)) return null;
            const suffix = id.slice(root.length).replace(/^[_/]/, "");
            const n = parseInt(suffix, 10);
            return Number.isNaN(n) ? null : n;
          })
          .filter((n) => n !== null);
        return Math.max(5, ...used) + 1;
      };

      if (isYLS) {
        // Principal ID ends in /0 — strip that last segment and replace with dependent's number
        const root = base.includes("/") ? base.substring(0, base.lastIndexOf("/")) : base;
        if (this.dependent.relationShipType === "Extra Dependent") {
          return root + "/" + nextExtraNum(root);
        }
        const num = relNumMap[this.dependent.relationShipType] ?? 1;
        return root + "/" + num;
      }

      if (this.dependent.relationShipType === "Extra Dependent") {
        return base + "_" + nextExtraNum(base);
      }

      const num = relNumMap[this.dependent.relationShipType] ?? 1;
      return base + "_" + num;
    },

    filteredRelationships() {
      // Collect relationship types already in use, excluding the one being edited
      const usedRels = new Set(
        (this.dependents || [])
          .filter(d => !this.edit || d.id !== this.dependent_id)
          .map(d => d.relationShipType)
      );

      // These can only be assigned once per principal
      const singleUse = ["Spouse", "Child 1", "Child 2", "Child 3", "Child 4"];

      return this.relationships.filter(rel => {
        if (singleUse.includes(rel) && usedRels.has(rel)) return false;
        if (rel === "Extra Dependent" && agency.id == "930972") return false;
        return true;
      });
    },

    requiresDependentPayment() {
      if (this.edit) return false;
      if (this.user && this.user.user_role == 1) return false;
      if (String(agency.id) !== "313923") return false;
      if (this.paymentSessionActive) return false;
      if (this.dependent.relationShipType === "Extra Dependent") return true;
      if (!this.client || !this.client.created_at) return false;
      const today = new Date().toISOString().slice(0, 10);
      const enrollmentDate = new Date(this.client.created_at).toISOString().slice(0, 10);
      return today !== enrollmentDate;
    },

    dependentPaymentAmount() {
      return this.dependent.relationShipType === "Extra Dependent" ? 9000 : 500;
    },

    // Once a dependent payment succeeds, the client gets a 1-hour window
    // to add further dependents without paying again per-dependent.
    paymentSessionActive() {
      const startedAt = Number(localStorage.getItem(this.paymentSessionKey()));
      if (!startedAt) return false;
      return Date.now() - startedAt < this.paymentSessionDurationMs;
    },

    paymentSessionMinutesLeft() {
      const startedAt = Number(localStorage.getItem(this.paymentSessionKey()));
      if (!startedAt) return 0;
      const remainingMs = this.paymentSessionDurationMs - (Date.now() - startedAt);
      return remainingMs > 0 ? Math.ceil(remainingMs / 60000) : 0;
    },

    dependentPaymentReference() {
      return randomAlphaNumeric(20);
    },

    dependentValidationError() {
      if (!this.client || !this.client.id_card_number) return "Client information is still loading. Please wait.";
      if (!this.dependent.relationShipType) return "Please select a relationship type.";
      if (!this.dependent.firstname) return "First name is required.";
      if (this.dependent.relationShipType === "Spouse" && !this.dependent.lastname) return "Surname is required for spouse.";
      if (!this.dependent.gender) return "Please select a gender.";
      if (!this.dependent.dob) return "Date of birth is required.";
      if (this.dependent.relationShipType === "Spouse" && this.getAge < 18) {
        return "Spouse must be at least 18 years old.";
      }
      if (
        this.getAge >= 21 &&
        this.dependent.relationShipType !== "Spouse" &&
        this.dependent.relationShipType !== "Extra Dependent"
      ) {
        return "Dependent must be under 21 years old.";
      }
      return null;
    },
  },
  methods: {
    async takePicAndroid() {
      try {
        const image = await Camera.getPhoto({
          quality: 70,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Prompt,
          format: "png",
        });
        if (!(await this.cropPhoto("data:image/png;base64," + image.base64String))) return;
        this.$toasted.success("Photo captured!", { position: "top-center", duration: 2000 });
      } catch (err) {
        if (err && err.message !== "User cancelled photos app") {
          this.$toasted.error("Could not access camera.", { position: "top-center", duration: 3000 });
        }
      }
    },

    attachPic(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => this.cropPhoto(e.target.result);
      reader.readAsDataURL(file);
    },

    // Opens the cropper; the cropped JPEG is saved through uploadDependantImage/canvas.
    // Keeps the uncropped photo so it can be adjusted again.
    async cropPhoto(src) {
      const cropped = await this.$refs.photoCropper.open(src);
      if (!cropped) return false;
      this.photoOriginal = src;
      this.dependent.user_image = cropped;
      this.upload_pic = false;
      return true;
    },

    removePhoto() {
      this.dependent.user_image = "";
      this.photoOriginal = "";
    },

    uploadPicture(dependent_id) {
      const formData = new FormData();
      formData.append("user_image", this.image);
      formData.append("dependant_id", dependent_id);
      this.axios.post("/api/v1/auth/uploadDependantImage", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },

    savePic(dependent_id) {
      if (!this.dependent.user_image) return;
      this.axios
        .post(`/api/v1/auth/uploadDependantImage/canvas`, {
          image: this.dependent.user_image,
          dependant_id: dependent_id,
        })
        .then(() => { this.getDependents(); })
        .catch((e) => { console.error(e); });
    },

    getDependents() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/allDependantUser/${this.$route.params.id}`)
        .then((response) => { this.dependents = response.data.data; })
        .catch((e) => { console.error(e); });
    },

    submitForm() {
      if (this.dependentValidationError) {
        this.$toasted.error(this.dependentValidationError, { position: "top-center", duration: 3000 });
        return;
      }
      this.addDependant();
    },

    onDependentPaymentSuccess() {
      this.pendingPaymentRef = this.dependentPaymentReference;
      this.startPaymentSession();
      this.addDependant();
    },

    onDependentPaymentClose() {
      console.log("Payment closed");
    },

    paymentSessionKey() {
      return `dependent_payment_session_${this.$route.params.id}`;
    },

    startPaymentSession() {
      localStorage.setItem(this.paymentSessionKey(), String(Date.now()));
    },

    logDependentPayment() {
      this.axios
        .post("/api/v1/make/transaction", {
          agency_id: agency.id,
          amount: this.dependentPaymentAmount,
          description: "Dependent Enrollment Payment",
          type: "online",
          status: "created",
          transaction_ref: this.pendingPaymentRef,
          user_id: this.$route.params.id,
        })
        .catch((e) => console.error(e));
      this.pendingPaymentRef = null;
    },

    isExtraSelector() {
      this.dependent.is_addtional = this.dependent.relationShipType === "Extra Dependent" ? "1" : "0";
    },

    addDependant() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;

      const payload = {
        firstname: this.dependent.firstname,
        lastname: this.dependent.lastname,
        middle_name: this.dependent.middle_name,
        institution_attending: this.dependent.institution_attending,
        relationShipType: this.dependent.relationShipType,
        user_id: this.$route.params.id,
        email: this.dependent.email,
        phone_number: this.dependent.phone_number,
        gender: this.dependent.gender,
        blood: this.dependent.blood,
        dob: this.dependent.dob,
        nimc_number: this.dependent.nimc_number,
        state: agency.state,
        lga: this.client.localgovt,
        is_addtional: this.dependent.is_addtional,
      };

      if (!this.edit) {
        this.axios
          .post("/api/v1/auth/addDependant", {
            ...payload,
            expiry_date: this.getExpiry,
            provider: this.user.provider_id,
            enrolled_by: this.user.id,
            agency_id: agency.id,
            id_card_number: this.getIdNum,
          })
          .then((response) => {
            const dependent_id = response.data.dependent.id;
            const wasFileUpload = this.upload_pic;
            const capturedImage = this.dependent.user_image;
            if (this.pendingPaymentRef) {
              this.logDependentPayment();
            }
            this.clearIt();
            if (wasFileUpload) {
              this.uploadPicture(dependent_id);
            } else if (capturedImage) {
              // savePic uses this.dependent.user_image which clearIt() just wiped,
              // so call the API directly with the saved value
              this.axios.post(`/api/v1/auth/uploadDependantImage/canvas`, {
                image: capturedImage,
                dependant_id: dependent_id,
              }).then(() => { this.getDependents(); }).catch(e => console.error(e));
            }
            this.isLoading = false;
            this.$toasted.success("Dependent added successfully!", { position: "top-center", duration: 3000 });
            this.getDependents();
          })
          .catch((error) => {
            this.isLoading = false;
            const errors = error.response && error.response.data && error.response.data.errors;
            this.$toasted.error(errors ? JSON.stringify(errors) : "Failed to add dependent.", { position: "top-center", duration: 4000 });
          });
      } else {
        this.axios
          .put(`/api/v1/auth/editDependant/${this.dependent_id}`, {
            ...payload,
            expiry_date: this.dependent.expiry_date,
            provider: this.dependent.provider,
          })
          .then(() => {
            const dep_id = this.dependent_id;
            this.upload_pic ? this.uploadPicture(dep_id) : this.savePic(dep_id);
            this.edit = false;
            this.isLoading = false;
            this.$toasted.success("Dependent updated successfully!", { position: "top-center", duration: 3000 });
            this.getDependents();
            this.clearIt();
          })
          .catch((error) => {
            this.isLoading = false;
            const errors = error.response && error.response.data && error.response.data.errors;
            this.$toasted.error(errors ? JSON.stringify(errors) : "Failed to update dependent.", { position: "top-center", duration: 4000 });
          });
      }
    },

    deleteDep(dependent) {
      if (confirm(`Delete ${dependent.full_name}? This cannot be undone.`)) {
        this.axios
          .delete(`/api/v1/auth/deletedependent/${dependent.id}`)
          .then(() => {
            this.$toasted.success("Dependent deleted.", { position: "top-center", duration: 3000 });
            this.getDependents();
          })
          .catch((e) => {
            console.error(e);
            this.$toasted.error("Delete failed.", { position: "top-center", duration: 3000 });
          });
      }
    },

    editDep(dep) {
      this.edit = true;
      this.dependent_id = dep.id;
      this.dependent.firstname = dep.firstname;
      this.dependent.lastname = dep.lastname;
      this.dependent.middle_name = dep.middle_name;
      this.dependent.relationShipType = dep.relationShipType;
      this.dependent.gender = dep.gender;
      this.dependent.dob = dep.dob;
      this.dependent.blood = dep.blood;
      this.dependent.expiry_date = dep.expiry_date;
      this.dependent.provider = dep.provider;
      this.dependent.nimc_number = dep.nimc_number;
      this.dependent.phone_number = dep.phone_number;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    clearIt() {
      this.edit = false;
      this.dependent_id = "";
      this.dependent.user_image = "";
      this.photoOriginal = "";
      this.dependent.firstname = "";
      this.dependent.lastname = "";
      this.dependent.middle_name = "";
      this.dependent.email = "";
      this.dependent.phone_number = "";
      this.dependent.relationShipType = "";
      this.dependent.gender = "";
      this.dependent.institution_attending = "";
      this.dependent.dob = "";
      this.dependent.expiry_date = "";
      this.upload_pic = false;
    },

    singleClient() {
      this.axios
        .get(`/api/v1/auth/user/zam/${this.$route.params.id}`)
        .then((response) => { this.client = response.data.user; })
        .catch((e) => { console.error(e); });
    },
  },
  created() {
    this.getDependents();
    this.singleClient();
  },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.page-title { font-size: 1.6rem; font-weight: 700; margin: 0; color: #fff; }
.page-subtitle { opacity: .8; margin: 4px 0 0; font-size: .9rem; color: #fff; }
.mt-n3 { margin-top: -16px; }

/* ── Form Card ── */
.form-card { border-radius: 14px; overflow: hidden; border: none; }
.form-card-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 28px;
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
}
.form-close-btn {
  background: rgba(255,255,255,.15); border: none; border-radius: 8px;
  color: #fff; width: 34px; height: 34px; cursor: pointer; font-size: 1rem;
  display: flex; align-items: center; justify-content: center;
}
.form-close-btn:hover { background: rgba(255,255,255,.28); }

/* ── Form Sections ── */
.form-section { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #f1f5f9; }
.form-section:last-of-type { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.form-section-title {
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; color: #374151; font-size: .9rem; margin-bottom: 16px;
}
.section-number {
  width: 26px; height: 26px; border-radius: 50%;
  background: linear-gradient(135deg, #1e3a5f, #2563eb); color: #fff;
  font-size: .75rem; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }
.styled-select {
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  padding: 9px 12px; font-size: .88rem;
}
.styled-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

/* Photo placeholder */
.upload-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  width: 100px; height: 120px; border-radius: 10px;
  border: 2px dashed #e2e8f0; background: #f8fafc; text-align: center;
}

/* ── Buttons ── */
.btn-create {
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: #fff; border: none; border-radius: 8px; padding: 10px 24px; font-weight: 600;
}
.btn-create:hover { opacity: .9; color: #fff; }
.btn-cancel { background: #f1f5f9; border: none; border-radius: 8px; padding: 10px 20px; color: #475569; font-weight: 600; }

/* ── Dependents Table Card ── */
.codes-card { border-radius: 12px; border: none; }
.card-header-custom {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #f1f5f9;
  background: #fff; border-radius: 12px 12px 0 0;
}
.card-header-custom h5 { font-weight: 600; color: #1e3a5f; }

.codes-table { font-size: .9rem; }
.codes-table thead th {
  background: #f8fafc; color: #64748b;
  font-weight: 600; font-size: .75rem;
  text-transform: uppercase; letter-spacing: .06em;
  border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}
.codes-table tbody td { padding: 12px 16px; vertical-align: middle; border-color: #f1f5f9; }
.code-row:hover { background: #f8fafc; }

/* Avatar */
.dep-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  object-fit: cover; border: 2px solid #e2e8f0;
}

/* Badges */
.rel-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: .75rem; font-weight: 600;
  background: #dbeafe; color: #1d4ed8;
}
.blood-badge {
  display: inline-block; padding: 2px 8px; border-radius: 20px;
  font-size: .75rem; font-weight: 600;
  background: #fee2e2; color: #991b1b;
}

/* View button */
.btn-view {
  border-radius: 6px; border: 1px solid #e2e8f0;
  color: #1e3a5f; background: #fff; font-size: .8rem;
}
.btn-view:hover { background: #f1f5f9; }

/* Empty state */
.empty-state { padding: 48px !important; color: #94a3b8; }
</style>
